import React from 'react';
import PropTypes from 'prop-types';
import { isType, flattenChildren } from '../../../../utils/react-children-utils/react-children-utils';
import { getValidationSummary, initFieldIsValids } from '../../../../utils/form-utils';
import ControlP from '../../../atoms/control/ControlP';
import SubmitButton from '../../../atoms/submit-button/index.ts';
import styles from './styles.scss';
import InputContainer from '../../../atoms/inputs/input-container';

class EditGroupForm extends React.Component {
    constructor(props) {
        super(props);

        this.setFieldState = this.setFieldState.bind(this);
        this.handlePageUrl = this.handlePageUrl.bind(this);
        this.handleLogoUpload = this.handleLogoUpload.bind(this);
        this.getControl = this.getControl.bind(this);
        this.handleTeamNameChange = this.handleTeamNameChange.bind(this);
        this.handleTeamName = this.handleTeamName.bind(this);

        const pageUrl = flattenChildren(props.children).find((c) => c.props
            && c.props.name === 'PageUrl'
            && isType(c, 'input'))
            .props.defaultValue;

        const defaultName = flattenChildren(props.children).find((c) => c.props
            && c.props.name === 'TeamName'
            && isType(c, 'input'))
            .props.defaultValue;

        const fieldIsValids = initFieldIsValids(
            flattenChildren(props.children)
                .filter((c) => isType(c, 'ControlP')),
        );

        this.state = {
            fieldIsValids,
            defaultPageUrl: pageUrl,
            pageUrl,
            teamName: defaultName,
        };
    }

    handlePageUrl(inputContainer) {
        const { pageUrl, defaultPageUrl } = this.state;
        const { name } = inputContainer.props;

        const children = flattenChildren(inputContainer.props.children);
        const pageUrlInput = children.find((c) => isType(c, 'input'));
        const otherEls = children.filter((c) => !isType(c, 'input'))
            .map((c) => React.cloneElement(c, { key: `${name}-${c.type?.name ?? c.type}` }));

        const newInput = (
            <input
                key={name}
                className={pageUrlInput.props.className}
                value={pageUrl ?? defaultPageUrl}
                name={pageUrlInput.props.name}
                placeholder={pageUrlInput.props.placeholder}
                readOnly
            />
        );

        return [newInput].concat(otherEls);
    }

    handleTeamName(inputContainer) {
        const { teamName } = this.state;
        const children = flattenChildren(inputContainer.props.children);
        const { name } = inputContainer.props;

        const teamNameInput = children
            .find((c) => isType(c, 'input'));
        const otherEls = children.filter((c) => !isType(c, 'input'))
            .map((c) => React.cloneElement(c, { key: `${name}-${c.type?.name ?? c.type}` }));

        const newInput = (
            <input
                key={name}
                className={teamNameInput.props.className}
                defaultValue={teamName}
                name={teamNameInput.props.name}
                onChange={this.handleTeamNameChange}
                placeholder={teamNameInput.props.placeholder}
                readOnly
            />
        );

        return [newInput].concat(otherEls);
    }

    handleTeamNameChange(event) {
        event.preventDefault();
        const teamName = event.target.value;

        const sanitised = teamName.toLowerCase()
            .replace(/[\s]/g, '-')
            .replace(/[^A-Za-z0-9_\\ -]+/g, '')
            .replace(/[^A-Za-z0-9_\\.~]+/g, '-');

        this.setState((s) => ({
            pageUrl: `${s.defaultPageUrl}${sanitised}`,
            teamName,
        }));
    }

    handleLogoUpload(inputContainer) {
        const children = flattenChildren(inputContainer.props.children);
        const { name } = inputContainer.props;

        const filepicker = children
            .find((c) => isType(c, 'S3FilePicker'));
        const otherEls = children.filter((c) => !isType(c, 'input'))
            .map((c) => React.cloneElement(c, { key: `${name}-${c.type?.name ?? c.type}` }));

        const newFilePicker = React.cloneElement(
            filepicker,
            {
                key: name,
                maxsize: 2,
                extensions: ['.png', '.jpg', '.jpeg'],
                passerror: (error) => { this.setFieldState('logourl', error); },
            },
        );

        return [newFilePicker].concat(otherEls);
    }

    getControl(control) {
        const currentControl = control;

        const controlChildren = flattenChildren(currentControl.props.children);

        const label = controlChildren.find((c) => isType(c, 'InputLabel'));
        const inputContainer = controlChildren.find((c) => isType(c, 'InputContainer'));

        let newInputs = inputContainer.props.children;
        let passError = this.setFieldState;

        // handle special inputs where we need to pass in more props
        if (inputContainer.props.name === 'pageurl') {
            newInputs = this.handlePageUrl(inputContainer);
            passError = undefined;
        } else if (inputContainer.props.name === 'logourl') {
            newInputs = this.handleLogoUpload(inputContainer);
        } else if (inputContainer.props.name === 'teamname') {
            newInputs = this.handleTeamName(inputContainer);
        }

        return (
            <ControlP key={`${inputContainer.props.name}-control`}>
                {label}
                <InputContainer
                    passerror={passError}
                    name={inputContainer.props.name}
                    displayname={inputContainer.props.displayname}
                    required={inputContainer.props.required}
                >
                    {newInputs}
                </InputContainer>
            </ControlP>
        );
    }

    setFieldState(fieldName, error) {
        const { fieldIsValids } = this.state;

        const newFields = fieldIsValids;
        newFields[fieldName] = !error;

        this.setState(() => ({
            fieldIsValids: newFields,
        }));
    }

    render() {
        const { children } = this.props;
        const { fieldIsValids } = this.state;

        const formError = children.find((c) => getValidationSummary(c));

        const invalid = Object.values(fieldIsValids).filter((c) => !c).length > 0;

        const controls = flattenChildren(children)
            .filter((c) => isType(c, 'ControlP'))
            .map((c) => this.getControl(c));

        return (
            <div className={styles['edit-group-form']}>
                {formError}
                <form method="post">
                    {controls}
                    <div className="columns is-centered">
                        <div className="column is-narrow">
                            <div className="control mt-4 has-text-centered">
                                <SubmitButton isdisabled={invalid} name="create-group-submit" label="Save changes" />
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        );
    }
}

EditGroupForm.propTypes = {
    children: PropTypes.node.isRequired,
};

EditGroupForm.defaultProps = {
};

export default EditGroupForm;
